/**
 * Harry system prompts — Normal (product guide) vs Harry (Harry Max voice).
 *
 * Voice calibration inputs (for a richer Harry mode later):
 *   - Sample Q&A or monologue recordings / transcripts
 *   - Harry's published writing (Managing Priorities excerpts)
 *   - Banned phrases and preferred vocabulary list
 *   - Example "good" vs "bad" replies for the same user question
 */

import { catalogPromptLines } from '../navigator/catalog';

export type HarryVoiceMode = 'harry' | 'normal';

const CAPABILITY_CATALOG = catalogPromptLines();

const SHARED_RULES = `You help people use Priorities.ai — a workspace for capturing items, doing item work, running prioritization sessions, and publishing a plan of record.

You have a catalog of jobs the product supports. When the user asks what to do, pick the best matching capability ids and explain briefly how to proceed. Prefer pointing to in-app destinations over generic advice.

Capability catalog:
${CAPABILITY_CATALOG}

Respond with JSON only:
{
  "reply": "1-3 short paragraphs answering the user",
  "capabilityIds": ["id-from-catalog", "..."]
}

If nothing matches, return an empty capabilityIds array and suggest browsing the workspace or rephrasing.`;

/**
 * Capability appendix for the canon-aware instruction stack (L2 appendix):
 * the routing catalog + output contract without the legacy identity intro —
 * identity comes from L0/L1 and the agent card there.
 */
export const HARRY_CAPABILITY_APPENDIX = `ROUTING CATALOG
You have a catalog of jobs the product supports. When the user asks what to do, pick the best matching capability ids and explain briefly how to proceed. Prefer pointing to in-app destinations over generic advice.

Capability catalog:
${CAPABILITY_CATALOG}

Respond with JSON only:
{
  "reply": "1-3 short paragraphs answering the user",
  "capabilityIds": ["id-from-catalog", "..."]
}

If nothing matches, return an empty capabilityIds array and suggest browsing the workspace or rephrasing.`;

/** True when a stored prompt body is just one of the shipped defaults (no real workspace customization). */
export function isDefaultHarryPromptBody(body: string | null | undefined): boolean {
  const t = body?.trim();
  return !t || t === HARRY_VOICE_PROMPT.trim() || t === NORMAL_VOICE_PROMPT.trim();
}

export const HARRY_VOICE_PROMPT = `You are Harry — co-founder of Priorities.ai and author of Managing Priorities. You speak like a seasoned prioritization practitioner: plain language, concrete examples, light dry humor, zero jargon without explanation. You respect that people are busy and that bad prioritization is usually a clarity problem, not a motivation problem.

Tone: warm, direct, occasionally wry. Short sentences. You say "here's what I'd do" not "it is recommended that." You never sound like a generic chatbot or a legal disclaimer.

${SHARED_RULES}`;

export const NORMAL_VOICE_PROMPT = `You are Harry, the in-product guide for Priorities.ai. Be concise, neutral, and task-focused — like a great technical writer. No persona performance; just clear routing and how-to.

${SHARED_RULES}`;

export function defaultHarrySystemPrompt(mode: HarryVoiceMode): string {
  return mode === 'harry' ? HARRY_VOICE_PROMPT : NORMAL_VOICE_PROMPT;
}

/** Prefix stored in agent_settings.system_prompt to persist voice mode without a migration. */
const VOICE_TAG_RE = /^\[voice:(harry|normal)\]\n?/;

export function packHarrySettings(mode: HarryVoiceMode, promptBody: string): string {
  const body = promptBody.replace(VOICE_TAG_RE, '').trim();
  return `[voice:${mode}]\n${body}`;
}

export function unpackHarrySettings(stored: string | null | undefined): {
  voiceMode: HarryVoiceMode;
  promptBody: string;
} {
  if (!stored?.trim()) {
    return { voiceMode: 'normal', promptBody: NORMAL_VOICE_PROMPT };
  }
  const match = stored.match(VOICE_TAG_RE);
  if (!match) {
    return { voiceMode: 'normal', promptBody: stored.trim() };
  }
  return {
    voiceMode: match[1] as HarryVoiceMode,
    promptBody: stored.replace(VOICE_TAG_RE, '').trim(),
  };
}

export function buildHarryUserPrompt(params: {
  query: string;
  pageTitle: string;
  pageOverview: string;
  routeCapabilities: string[];
}): string {
  return [
    `Current page: ${params.pageTitle}`,
    params.pageOverview,
    params.routeCapabilities.length
      ? `Jobs especially relevant on this page: ${params.routeCapabilities.join(', ')}`
      : '',
    `User question: ${params.query}`,
  ].filter(Boolean).join('\n\n');
}

/** Instruction appended for spin-the-wheel Harry-isms (quotes live in workspace system prompt). */
export const HARRY_ISM_SPIN_APPENDIX = `
When asked to spin a Harry-ism, draw from the Harry quotes, aphorisms, and canon in this system prompt.
Pick or lightly adapt ONE line that fits the user's current page context — do not invent unrelated philosophy.
Respond with JSON only: { "ism": "one or two sentences in Harry's voice" }`;

export function buildHarryIsmSpinPrompt(params: {
  pageTitle: string;
  pageOverview: string;
  pathname: string;
  recentQuery?: string | null;
}): string {
  return [
    'Spin the wheel: give me one contextual Harry-ism for right now.',
    `Current page: ${params.pageTitle}`,
    params.pageOverview,
    `URL path: ${params.pathname}`,
    params.recentQuery ? `They were just asking about: ${params.recentQuery}` : '',
  ].filter(Boolean).join('\n\n');
}
